import { lightTheme, Provider } from "@adobe/react-spectrum";
import { IEnvironment } from "@identity/imslib/adobe-id/IEnvironment";
import * as React from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as JSONFormsCore from "@jsonforms/core";
import * as JSONFormsReact from "@jsonforms/react";

// Redux
import { Provider as ReduxProvider } from "react-redux";

// Redux store
import { setInitialValue } from "./redux/actions/SetReduxStateAction";
import { dispatchReduxAction } from "./redux/DispatchReduxAction";
import store from "./store";

import APITemplate from "./components/APITemplate/APITemplate";
import MultiplePage from "./components/MultiplePage/MultiplePage";
import SinglePage from "./components/SinglePage/SinglePage";
import { FormProvider, FormContext } from "./context/FormsContext";
import { PageContext } from "./context/PageContext";
import Loader from "./components/Loader/Loader";
import Modal from "./components/Modal/Modal";
import displayToasts from "./utils/displayToasts";
import "./styles.scss";
import {
  APITemplateProps,
  DynamicFormProps,
  Pages,
  PageType,
  SinglePageProps,
} from "./types/Pages";
import {
  printSchemaVersion,
  addCommonJSONHeader,
  addCommonComponent,
} from "./utils/commonUtils";
import {
  ADOBE_CLEAN_CSS,
  DEMO_IMS_CLIENT_ID,
  DYNAMIC_FORMS,
  ENABLE_LOGS,
  IMS_LIB_JS_PROD,
  IMS_LIB_JS_STAGE,
} from "./utils/constants";
import dispatchWindowEvent from "./utils/dispatchWindowEvent";
import { validateJSON } from "./validator/index";
import * as konsole from "./utils/konsole";
import RouterWrapper from "./components/RouterWrapper/RouterWrapper";
import useFetch from "./hooks/useFetch";
import LocaleWrapper from "./components/LocaleWrapper/LocaleWrapper";

declare const window: any;

window.enableLogs = () => {
  try {
    localStorage.setItem(ENABLE_LOGS, JSON.stringify(true));
  } catch (error) {
    /* empty */
  }
};

window.disableLogs = () => {
  try {
    localStorage.setItem(ENABLE_LOGS, JSON.stringify(false));
  } catch (error) {
    /* empty */
  }
};

export const loadTypeKitCss = () => {
  const element = document.createElement("link");
  element.setAttribute("rel", "stylesheet");
  element.setAttribute("type", "text/css");
  element.setAttribute("href", ADOBE_CLEAN_CSS);
  document.getElementsByTagName("head")[0].appendChild(element);
};

// export type AppStore = ReturnType<typeof setupStore>
// export function setupStore(preloadedState?: PreloadedState<RootState>) {
//   return configureStore({
//     reducer: reducers,
//     middleware: (getDefaultMiddleware) => getDefaultMiddleware({ serializableCheck: false })
//       .concat([thunk]),
//     preloadedState,
//   });
// }
// export const store = setupStore();

function App({
  pages,
  storybook = false,
  renderers,
  type = PageType.MULTIPLE_PAGE,
  lifecycleHooks = {},
  commonJsonHeaders = {},
  dynamicData = {},
  currentEnvironment = "dev",
  defaultRoute = "",
  includeAdvancedRenderers = false,
  locale = "en_US",
  defaultLocale = "en_US",
  config = {},
}: DynamicFormProps) {
  if (pages?.version) {
    printSchemaVersion(pages.version);
  }

  let imsData: any;
  window.DynamicForms = {};

  let returnJSX = null;
  let pagesObj = { ...pages };
  pagesObj = addCommonJSONHeader(pages, commonJsonHeaders, currentEnvironment);
  switch (type) {
    case PageType.MULTIPLE_PAGE:
      imsData = (pagesObj as Pages)?.options?.imsData;
      window.imsConfig = imsData;
      window.DynamicForms.pages = pagesObj;
      returnJSX = (
        <MultiplePage
          pages={pagesObj}
          storybook={storybook}
          renderers={renderers}
          lifecycleHooks={lifecycleHooks}
          commonJsonHeaders={commonJsonHeaders}
          includeAdvancedRenderers={includeAdvancedRenderers}
        />
      );
      break;
    case PageType.SINGLE_PAGE:
      imsData = (pagesObj as SinglePageProps)?.options?.imsData;
      window.imsConfig = imsData;
      window.DynamicForms.pages = pagesObj;
      returnJSX = (
        <SinglePage
          pages={pagesObj}
          renderers={renderers}
          lifecycleHooks={lifecycleHooks}
          commonJsonHeaders={commonJsonHeaders}
          dynamicData={dynamicData}
          includeAdvancedRenderers={includeAdvancedRenderers}
        />
      );
      break;
    case PageType.API_TEMPLATE:
      {
        const pageTemplate = pagesObj as APITemplateProps;
        if (pageTemplate.isMultiPage) {
          imsData = (pagesObj as Pages)?.options?.imsData;
        } else {
          imsData = (pagesObj as SinglePageProps)?.options?.imsData;
        }
        window.imsConfig = imsData;
        window.DynamicForms.pages = pagesObj;
        returnJSX = (
          <APITemplate
            pages={pagesObj as APITemplateProps}
            storybook={storybook}
            lifecycleHooks={lifecycleHooks}
            commonJsonHeaders={commonJsonHeaders}
            renderers={renderers}
            dynamicData={dynamicData}
            currentEnvironment={currentEnvironment}
            defaultRoute={defaultRoute}
            includeAdvancedRenderers={includeAdvancedRenderers}
          />
        );
      }
      break;
    default:
      imsData = (pagesObj as SinglePageProps)?.options?.imsData;
      window.imsConfig = imsData;
      window.DynamicForms.pages = pagesObj;
      returnJSX = (
        <SinglePage
          pages={pagesObj}
          renderers={renderers}
          lifecycleHooks={lifecycleHooks}
          commonJsonHeaders={commonJsonHeaders}
          dynamicData={dynamicData}
          includeAdvancedRenderers={includeAdvancedRenderers}
        />
      );
      break;
  }

  const [readyToRender, setReadyToRender] = React.useState(false);

  const getRedirectUri = () => {
    if (imsData.redirectBackToSamePage) {
      return window.location.href;
    }
    if (imsData.redirect_uri) {
      return window.location.origin + imsData.redirect_uri;
    }
    return window.location.origin;
  };

  React.useEffect(() => {
    loadTypeKitCss();
    if (imsData) {
      // IMS Configuration
      window.adobeid = {
        client_id: imsData.client_id || DEMO_IMS_CLIENT_ID,
        environment: imsData.environment || IEnvironment.STAGE,
        scope: imsData.scope || "AdobeID,openid",
        locale: imsData.locale || "en_US",
        redirect_uri: getRedirectUri(),
        async onReady() {
          konsole.log(`${DYNAMIC_FORMS}: IMS Auth Loaded`);
          dispatchWindowEvent("adobeIMS:profileReady");
          setReadyToRender(true);
          if (lifecycleHooks?.onSuccessCallbacks) {
            const callbacks: any = lifecycleHooks?.onSuccessCallbacks;
            const imsCallback = callbacks?.imsCallback;
            if (imsCallback) {
              imsCallback.onIMSCallReady(setReadyToRender);
            }
          }
        },
      };
      // Load IMS
      const imsLib = document.createElement("script");
      imsLib.src =
        imsData.environment === IEnvironment.PROD
          ? IMS_LIB_JS_PROD
          : IMS_LIB_JS_STAGE;
      document.body.appendChild(imsLib);

      // Fix navigation issue, when switching routes, IMS onReady will not be called again.
      // If IMS exists, then set readyToRender to true
      if (window?.adobeIMS?.initialized) {
        setReadyToRender(true);
      }
    } else {
      setReadyToRender(true);
    }
  }, [imsData]);

  store.dispatch(
    setInitialValue({
      locale,
      defaultLocale,
      ...config?.reduxState,
    })
  );
  return readyToRender ? (
    <RouterWrapper>
      <Provider theme={lightTheme} colorScheme="light">
        <ReduxProvider store={store}>
          <FormProvider
            initialValues={{
              config: {
                formTouched: false,
              },
            }}
          >
            <LocaleWrapper locale={locale} defaultLocale={defaultLocale}>
              {returnJSX}
              <ToastContainer position="top-center" />
            </LocaleWrapper>
          </FormProvider>
        </ReduxProvider>
      </Provider>
    </RouterWrapper>
  ) : (
    <Loader fullScreen />
  );
}

export default App;

const hooks = { useFetch };

export {
  Pages,
  DynamicFormProps,
  SinglePageProps,
  APITemplateProps,
  PageType,
  validateJSON,
  Loader,
  displayToasts,
  JSONFormsCore,
  addCommonComponent,
  PageContext,
  FormContext,
  hooks,
  JSONFormsReact,
  Modal,
  store as ReduxStore,
  dispatchReduxAction,
};
